// @catalog F.030
// TabCatalogo — Productos en la misma categoría LLM (competidores).
// Hace fetch usando los datos ya cargados del dialog. Tabla simple con
// SKU + nombre + ABC + inventario + estado.

import { Alert, Box, CircularProgress, Stack, Typography } from "@mui/material";
import type { CategoryCompetitorsData } from "../productDetailTypes";
import { ABC_TONE, TONE_BG_VAR, TONE_COLOR_VAR, fmtNum } from "./helpers";

interface TabCatalogoProps {
  competitors: CategoryCompetitorsData | null;
  loading: boolean;
}

export function TabCatalogo({ competitors, loading }: TabCatalogoProps) {
  if (loading) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress size={20} />
      </Stack>
    );
  }

  if (!competitors) {
    return (
      <Alert severity="info" variant="outlined">
        Sin datos de catálogo disponibles para este producto.
      </Alert>
    );
  }

  if (!competitors.competitors.length) {
    return (
      <Box
        sx={{
          py: 4,
          textAlign: "center",
          color: "var(--ink-3)",
          fontSize: 13,
        }}
      >
        {competitors.category
          ? "Sin otros productos en esta categoría."
          : "Producto sin categoría LLM asignada."}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: 1.25,
        p: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.75 }}
      >
        <Typography
          sx={{
            fontSize: 12,
            color: "var(--ink-3)",
            fontWeight: 500,
            letterSpacing: "0.02em",
          }}
        >
          Productos en{" "}
          <Box
            component="span"
            sx={{ color: "var(--ink-1)", fontWeight: 600 }}
          >
            {competitors.category}
          </Box>
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: 12,
          color: "var(--ink-3)",
          mb: 1.25,
        }}
      >
        {competitors.competitors.length} producto
        {competitors.competitors.length === 1 ? "" : "s"} compiten en la misma
        categoría. Cuidado con canibalizar stock al comprar más.
      </Typography>
      <Box
        component="table"
        sx={{ width: "100%", borderCollapse: "collapse" }}
      >
        <Box component="thead">
          <Box component="tr">
            <Box
              component="th"
              sx={{
                textAlign: "left",
                fontSize: 11.5,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--ink-3)",
                py: 1.25,
                px: 1.5,
                borderBottom: "1px solid var(--border)",
                bgcolor: "var(--bg)",
              }}
            >
              Producto
            </Box>
            <Box
              component="th"
              sx={{
                textAlign: "left",
                fontSize: 11.5,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--ink-3)",
                py: 1.25,
                px: 1.5,
                borderBottom: "1px solid var(--border)",
                bgcolor: "var(--bg)",
              }}
            >
              ABC
            </Box>
            <Box
              component="th"
              sx={{
                textAlign: "right",
                fontSize: 11.5,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--ink-3)",
                py: 1.25,
                px: 1.5,
                borderBottom: "1px solid var(--border)",
                bgcolor: "var(--bg)",
              }}
            >
              Inventario
            </Box>
            <Box
              component="th"
              sx={{
                textAlign: "left",
                fontSize: 11.5,
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: "0.04em",
                color: "var(--ink-3)",
                py: 1.25,
                px: 1.5,
                borderBottom: "1px solid var(--border)",
                bgcolor: "var(--bg)",
              }}
            >
              Estado
            </Box>
          </Box>
        </Box>
        <Box component="tbody">
          {competitors.competitors.map((c) => {
            const abcTone = ABC_TONE[c.abcClassification ?? ""] ?? "neutral";
            return (
              <Box
                key={c.unifiedProductId}
                component="tr"
                sx={{ "&:hover": { bgcolor: "var(--bg)" } }}
              >
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    px: 1.5,
                    borderBottom: "1px solid var(--border)",
                    fontSize: 13,
                  }}
                >
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <Box
                      component="span"
                      sx={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color: "var(--ink-3)",
                        bgcolor: "var(--bg-2)",
                        px: 0.625,
                        py: 0.25,
                        borderRadius: 0.5,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {c.unifiedCode || "—"}
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: "var(--ink-1)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        minWidth: 0,
                      }}
                    >
                      {c.unifiedName}
                    </Typography>
                  </Stack>
                </Box>
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    px: 1.5,
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  {c.abcClassification ? (
                    <Box
                      component="span"
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        px: 0.875,
                        py: 0.25,
                        borderRadius: 0.5,
                        fontSize: 10,
                        fontWeight: 500,
                        bgcolor: TONE_BG_VAR[abcTone],
                        color: TONE_COLOR_VAR[abcTone],
                      }}
                    >
                      {c.abcClassification}
                    </Box>
                  ) : (
                    <Box component="span" sx={{ color: "var(--ink-4)" }}>
                      —
                    </Box>
                  )}
                </Box>
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    px: 1.5,
                    borderBottom: "1px solid var(--border)",
                    textAlign: "right",
                    fontVariantNumeric: "tabular-nums",
                    fontSize: 13,
                    color: "var(--ink-1)",
                  }}
                >
                  {fmtNum(c.totalInventory)}
                </Box>
                <Box
                  component="td"
                  sx={{
                    py: 1.25,
                    px: 1.5,
                    borderBottom: "1px solid var(--border)",
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 0.875,
                      py: 0.25,
                      borderRadius: 0.5,
                      fontSize: 10,
                      fontWeight: 500,
                      bgcolor: c.activeForPurchase
                        ? "var(--pos-soft)"
                        : "var(--bg-2)",
                      color: c.activeForPurchase ? "var(--pos)" : "var(--ink-2)",
                      border: c.activeForPurchase
                        ? "none"
                        : "1px solid var(--border)",
                    }}
                  >
                    {c.activeForPurchase ? "Compras ON" : "OFF"}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default TabCatalogo;
